/**
 * Centralized date formatting utilities with timezone support.
 * 
 * ALL functions require an explicit timezone - NO FALLBACKS.
 * If timezone is missing, functions will throw to make issues visible.
 */
import { TZDate } from '@date-fns/tz';
import { format, formatDistanceToNow } from 'date-fns';

const ISO_PREFIX_RE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}/;
const TZ_SUFFIX_RE = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/**
 * Parse a UTC timestamp without applying any fallbacks.
 * @param {string|Date} dateValue - Date string or Date object
 * @returns {Date|null} - Parsed Date or null for missing values
 */
function parseUtcDate(dateValue) {
    if (dateValue === null || dateValue === undefined) return null;
    if (dateValue instanceof Date) {
        if (Number.isNaN(dateValue.getTime())) {
            throw new Error(`Invalid timestamp: ${String(dateValue)}`);
        }
        return dateValue;
    }
    if (typeof dateValue !== 'string') {
        throw new Error(`Invalid timestamp: ${String(dateValue)}`);
    }

    const dateStr = dateValue.trim();
    if (!dateStr) {
        throw new Error(`Invalid timestamp: ${String(dateValue)}`);
    }
    if (ISO_PREFIX_RE.test(dateStr) && !TZ_SUFFIX_RE.test(dateStr)) {
        throw new Error(`Timestamp missing timezone suffix: ${dateStr}`);
    }

    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) {
        throw new Error(`Invalid timestamp: ${String(dateValue)}`);
    }
    return date;
}

/**
 * Format a UTC date to user's local timezone.
 * @param {string|Date} utcDate - UTC date string or Date object
 * @param {string} formatStr - date-fns format string
 * @param {string} timezone - IANA timezone (required, no fallback)
 * @throws {Error} if timezone is not provided
 */
export function formatInTimezone(utcDate, formatStr, timezone) {
    if (!timezone) {
        throw new Error('formatInTimezone: timezone is required - check user profile');
    }
    if (utcDate === null || utcDate === undefined) return '';

    const date = parseUtcDate(utcDate);
    if (!date) return '';
    const tzDate = new TZDate(date, timezone);
    return format(tzDate, formatStr);
}

/**
 * Format relative time (e.g., "5 minutes ago").
 * Note: Relative time is timezone-independent (difference between two moments).
 * The timezone param is kept for API consistency but not used here.
 * @param {string|Date} utcDate - UTC date string or Date object
 * @param {string} timezone - IANA timezone (required for consistency, but not used)
 * @throws {Error} if timezone is not provided
 */
export function formatRelativeTime(utcDate, timezone) {
    if (!timezone) {
        throw new Error('formatRelativeTime: timezone is required - check user profile');
    }
    if (utcDate === null || utcDate === undefined) return '';

    // Relative time is timezone-independent - just use the raw UTC date
    // formatDistanceToNow compares against Date.now() which is also UTC-based
    const date = parseUtcDate(utcDate);
    if (!date) return '';
    return formatDistanceToNow(date, { addSuffix: true });
}

/**
 * Get current time in user's timezone.
 * @param {string} timezone - IANA timezone (required, no fallback)
 * @throws {Error} if timezone is not provided
 */
export function nowInTimezone(timezone) {
    if (!timezone) {
        throw new Error('nowInTimezone: timezone is required - check user profile');
    }
    return new TZDate(new Date(), timezone);
}

